import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import {
  PROPERTY_TYPES,
  PACKAGE_OPTIONS,
  BRAND,
} from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsultationContext = createContext(null);
export const useConsultation = () => useContext(ConsultationContext);

const emptyForm = { name: "", email: "", phone: "", property_type: "", package: "", message: "" };

const inputCls =
  "w-full bg-transparent border-b border-navy/20 py-2.5 text-navy placeholder:text-navy/30 outline-none focus:border-gold transition-colors duration-300";

export function LeadForm({ defaultPackage = "", onDone, dark = false }) {
  const [form, setForm] = useState({ ...emptyForm, package: defaultPackage });
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/leads`, form);
      if (res.data?.status === "success") {
        setStatus("success");
        toast.success("Thank you \u2014 we\u2019ll be in touch shortly.");
        setForm({ ...emptyForm, package: defaultPackage });
        onDone && onDone();
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please call us on " + BRAND.phone);
    }
  };

  const labelColor = dark ? "text-white/50" : "text-navy/50";
  const fieldCls = dark
    ? inputCls.replace(/navy/g, "white").replace("text-white", "text-white")
    : inputCls;

  if (status === "success") {
    return (
      <div className="py-12 text-center" data-testid="lead-form-success">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold text-navy">
          <Check className="h-7 w-7" />
        </div>
        <h3 className="mt-6 font-serif text-3xl text-current">Enquiry received</h3>
        <p className={`mt-3 text-sm ${dark ? "text-white/60" : "text-navy/60"}`}>
          Our team will reach out to you soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="lead-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <label className="block">
          <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${labelColor}`}>Full name</span>
          <input data-testid="lead-name-input" className={fieldCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
        </label>
        <label className="block">
          <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${labelColor}`}>Phone</span>
          <input data-testid="lead-phone-input" className={fieldCls} value={form.phone} onChange={set("phone")} placeholder="07 ..." />
        </label>
      </div>
      <label className="block">
        <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${labelColor}`}>Email</span>
        <input data-testid="lead-email-input" type="email" className={fieldCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
      </label>
      <div className="grid gap-6 sm:grid-cols-2">
        <label className="block">
          <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${labelColor}`}>Property type</span>
          <select data-testid="lead-property-select" className={`${fieldCls} appearance-none`} value={form.property_type} onChange={set("property_type")}>
            <option value="" className="text-navy">Select...</option>
            {PROPERTY_TYPES.map((p) => (<option key={p} value={p} className="text-navy">{p}</option>))}
          </select>
        </label>
        <label className="block">
          <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${labelColor}`}>Interested in</span>
          <select data-testid="lead-package-select" className={`${fieldCls} appearance-none`} value={form.package} onChange={set("package")}>
            <option value="" className="text-navy">Select...</option>
            {PACKAGE_OPTIONS.map((p) => (<option key={p} value={p} className="text-navy">{p}</option>))}
          </select>
        </label>
      </div>
      <label className="block">
        <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${labelColor}`}>Message (optional)</span>
        <textarea data-testid="lead-message-input" rows={3} className={fieldCls} value={form.message} onChange={set("message")} placeholder="Tell us about your property & goals" />
      </label>
      <button
        type="submit"
        data-testid="lead-submit-button"
        disabled={status === "loading"}
        className="group inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-navy transition-[transform,filter] duration-300 hover:brightness-105 hover:-translate-y-0.5 disabled:opacity-60"
      >
        {status === "loading" ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending</>) : "Send my enquiry"}
      </button>
    </form>
  );
}

export function ConsultationProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [pkg, setPkg] = useState("");

  const openWith = useCallback((packageName = "") => {
    setPkg(packageName || "");
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <ConsultationContext.Provider value={{ open: openWith, close }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="consultation-modal"
          >
            <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" onClick={close} />
            <motion.div
              className="relative z-10 w-full max-w-xl bg-bone p-8 sm:p-12 shadow-2xl"
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
            >
              <button onClick={close} data-testid="consultation-close" className="absolute right-5 top-5 text-navy/40 hover:text-navy transition-colors">
                <X className="h-5 w-5" />
              </button>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">Free Consultation</p>
              <h2 className="mt-3 font-serif text-4xl leading-tight text-navy">Let&rsquo;s talk about<br/>your property.</h2>
              <p className="mt-3 text-sm text-navy/55">No obligation. We&rsquo;ll reply within one working day.</p>
              <div className="mt-8">
                <LeadForm defaultPackage={pkg} onDone={() => setTimeout(close, 2200)} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ConsultationContext.Provider>
  );
}
